import { useInfiniteQuery } from '@tanstack/react-query';

import { api } from '../lib/api-client';

import type { ListMessagesResponse, MessageItem } from '../lib/types';

/**
 * useMessages(conversationId) — infinite query reversa para carregar
 * histórico mais antigo sob demanda.
 *
 * O endpoint do Engine retorna mensagens da mais recente para a mais
 * antiga (ORDER BY sentAt DESC). A UI inverte ao renderizar — mais
 * antigas no topo, mais recentes no fundo.
 */
/**
 * Fonte ÚNICA das chaves de cache de mensagens de uma conversa (Alvo D, F.5
 * C1): todo write otimista espelha nas duas — a clássica e a do fio contínuo.
 */
export function messagesQueryKeys(conversationId: string): readonly (readonly unknown[])[] {
  return [
    ['messages', conversationId],
    ['messages', conversationId, 'person-scope'],
  ];
}

export function useMessages(
  conversationId: string | undefined,
  /**
   * B2 (spec 2026-08-12 §3.4) — pede o transcript contínuo por pessoa. O
   * servidor DECIDE (flag ∧ capability ∧ regras duras); `personScopeApplied`
   * devolve a verdade da resposta — false = degradou para a conversa única.
   */
  personScope = false,
): {
  pages: ListMessagesResponse[];
  messages: MessageItem[];
  hasMore: boolean;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  personScopeApplied: boolean;
} {
  // ⚠️ CONTRATO DE CACHE (revisão F.5 r4, CRITICAL; endurecido no Alvo D):
  // o fluxo otimista de envio (`use-send-message.ts`) grava com `setQueryData`
  // — que NÃO faz prefix-match — em TODAS as chaves de `messagesQueryKeys`.
  // Com personScope OFF a chave segue a histórica (equivalência real); a
  // variante person-scope tem chave própria E TAMBÉM recebe os writes
  // otimistas (com o fio default ON — Alvo D — ela é o caminho principal).
  const queryKey = personScope
    ? (['messages', conversationId, 'person-scope'] as const)
    : (['messages', conversationId] as const);
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => {
      if (conversationId === undefined) throw new Error('conversation id required');
      const params: { cursor?: string; limit?: number; personScope?: boolean } = { limit: 30 };
      if (pageParam !== undefined) params.cursor = pageParam;
      if (personScope) params.personScope = true;
      return api.listMessages(conversationId, params);
    },
    enabled: conversationId !== undefined,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.meta.hasMore && lastPage.meta.nextCursor !== null
        ? lastPage.meta.nextCursor
        : undefined,
    staleTime: 30 * 1000,
  });

  const pages = query.data?.pages ?? [];
  // Mensagens em ordem cronológica: mais antigas no topo.
  // Cada página vem DESC; concatenamos as páginas (page 0 mais recente,
  // pages 1+ progressivamente mais antigas) e revertemos no final.
  const messages: MessageItem[] = pages
    .flatMap((p) => p.data)
    .slice()
    .sort((a, b) => {
      const diff = new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime();
      // Desempate por id — espelha o servidor (sentAt, id); sem ele, empates
      // cross-conversa re-embaralham a ordem e a divisória de episódio flapa.
      return diff !== 0 ? diff : a.id.localeCompare(b.id);
    });

  return {
    pages,
    messages,
    hasMore: query.hasNextPage ?? false,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => void query.fetchNextPage(),
    personScopeApplied: pages[0]?.meta.personScope === true,
  };
}
